import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type ListResponse } from '@lib/api';
import { track } from '@lib/posthog';
import type {
  CreateFirsatInput,
  Firsat,
  FirsatSesSonuc,
  KanbanGrubu,
  ListFirsatQuery,
} from '@/types/firsat';

const QUERY_KEY = 'firsatlar';
const KANBAN_KEY = 'firsatlar-kanban';

/** Kanban görünümü — aşamaya göre gruplanmış */
export function useFirsatKanban(query: ListFirsatQuery = {}) {
  return useQuery({
    queryKey: [KANBAN_KEY, query],
    queryFn: () =>
      api
        .get('firsatlar/kanban', {
          searchParams: Object.entries(query).reduce((acc, [k, v]) => {
            if (v !== undefined && v !== '') acc[k] = String(v);
            return acc;
          }, {} as Record<string, string>),
        })
        .json<{ data: KanbanGrubu[] }>()
        .then((r) => r.data),
  });
}

/** Düz liste (sayfalı) */
export function useFirsatlar(query: ListFirsatQuery = {}) {
  return useQuery({
    queryKey: [QUERY_KEY, query],
    queryFn: () =>
      api
        .get('firsatlar', {
          searchParams: Object.entries(query).reduce((acc, [k, v]) => {
            if (v !== undefined && v !== '') acc[k] = String(v);
            return acc;
          }, {} as Record<string, string>),
        })
        .json<ListResponse<Firsat>>(),
  });
}

export function useFirsat(id: string | undefined) {
  return useQuery({
    queryKey: [QUERY_KEY, id],
    queryFn: () =>
      api.get(`firsatlar/${id}`).json<{ data: Firsat }>().then((r) => r.data),
    enabled: !!id,
  });
}

export function useCreateFirsat() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateFirsatInput) =>
      api
        .post('firsatlar', { json: input })
        .json<{ data: Firsat }>()
        .then((r) => r.data),
    onSuccess: (data) => {
      track('firsat_olusturuldu', {
        firsat_id: data.id,
        tutar: data.tutar,
      });
      qc.invalidateQueries({ queryKey: [QUERY_KEY] });
      qc.invalidateQueries({ queryKey: [KANBAN_KEY] });
      qc.invalidateQueries({ queryKey: ['musteriler'] }); // müşteri detay sayfasındaki embedded listeyi tazele
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

/**
 * Konuşma metninden fırsat alanlarını tanır (müşteri/ürün/miktar). Kayıt YAPMAZ;
 * yalnızca formu ön-doldurmak için öneri döner.
 */
export function useFirsatSesTani() {
  return useMutation({
    mutationFn: (metin: string) =>
      api
        .post('firsatlar/ses-tani', { json: { metin } })
        .json<{ data: FirsatSesSonuc }>()
        .then((r) => r.data),
  });
}

export function useUpdateFirsat(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<CreateFirsatInput>) =>
      api
        .patch(`firsatlar/${id}`, { json: input })
        .json<{ data: Firsat }>()
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [QUERY_KEY] });
      qc.invalidateQueries({ queryKey: [KANBAN_KEY] });
      qc.invalidateQueries({ queryKey: [QUERY_KEY, id] });
    },
  });
}

export function useDeleteFirsat() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`firsatlar/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [QUERY_KEY] });
      qc.invalidateQueries({ queryKey: [KANBAN_KEY] });
    },
  });
}

/** Drag-drop: aşama + sıra güncelle */
export function useChangeAsama() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      asamaKod,
      sira,
    }: {
      id: string;
      asamaKod: string;
      sira: number;
    }) =>
      api
        .patch(`firsatlar/${id}/asama`, { json: { asamaKod, sira } })
        .json<{ data: Firsat }>()
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KANBAN_KEY] });
      qc.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

export function useKazanFirsat() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api
        .patch(`firsatlar/${id}/kazandi`)
        .json<{ data: Firsat }>()
        .then((r) => r.data),
    onSuccess: (data) => {
      track('firsat_kazanildi', {
        firsat_id: data.id,
        tutar: data.tutar,
      });
      qc.invalidateQueries({ queryKey: [KANBAN_KEY] });
      qc.invalidateQueries({ queryKey: [QUERY_KEY] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useKaybetFirsat() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      kayipNedeni,
      rakip,
    }: {
      id: string;
      kayipNedeni: string;
      rakip?: string;
    }) =>
      api
        .patch(`firsatlar/${id}/kaybetti`, { json: { kayipNedeni, rakip } })
        .json<{ data: Firsat }>()
        .then((r) => r.data),
    onSuccess: (data, variables) => {
      track('firsat_kaybedildi', {
        firsat_id: data.id,
        kayip_nedeni: variables.kayipNedeni,
        rakip: variables.rakip,
      });
      qc.invalidateQueries({ queryKey: [KANBAN_KEY] });
      qc.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}
